/**
 * Kleine client-side API-helper voor de server (Express op :3001).
 * De client praat via een relatief /api-pad; in dev stuurt de Vite-proxy dat door
 * naar http://localhost:3001, in productie draait de server achter hetzelfde origin.
 * Dus GEEN absolute URL hier.
 */
import { haalVersToken, sessieVerlopen, dataConflict } from '../auth/huidigeGebruiker.js';
import { leesActieveOmgeving } from './omgeving.js';

// Auth-header met een VERS ID-token. Async omdat het token zo nodig ververst wordt
// (MSAL acquireTokenSilent). Geen e-mailheader meer: de server vertrouwt alleen het token.
// Daarnaast de actieve omgeving als x-omgeving-header (alleen als er een is); de server
// valideert die tegen de identiteit. Dekt team-rooster/ruil/meldingen/diensten (+ later data).
async function authHeaders() {
  const token = await haalVersToken();
  const omgeving = leesActieveOmgeving();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(omgeving ? { 'x-omgeving': omgeving } : {}),
  };
}

const SESSIE_VERLOPEN_TEKST = 'Je sessie is verlopen — log opnieuw in.';

/**
 * Bouw de Error voor een niet-ok respons. Eén plek, zodat elke aanroeper dezelfde
 * behandeling krijgt (er zijn acht plekken in de UI die err.message rauw tonen).
 *
 * Een 401 betekent altijd: geen of verlopen token. Dan vuren we de centrale sessie-melding
 * af (App toont de overlay) en geven we een leesbare zin terug i.p.v. de technische
 * servertekst ("Ongeldig token: exp claim timestamp check failed" / "Niet ingelogd").
 * err.status blijft de echte HTTP-status, zodat bestaande checks (MijnRooster) blijven werken.
 */
function maakFout(status, melding) {
  const err = new Error(status === 401 ? SESSIE_VERLOPEN_TEKST : melding);
  err.status = status;
  if (status === 401) sessieVerlopen();
  return err;
}

/**
 * Gedeelde GET + foutafhandeling voor de rooster-endpoints.
 * @throws {Error} met een leesbare melding; bij een serverantwoord staat de HTTP-status op err.status.
 */
async function haalJson(pad, headers = {}) {
  let resp;
  try {
    resp = await fetch(pad, { headers });
  } catch {
    throw new Error('Kan de server niet bereiken. Draait de server (poort 3001)?');
  }

  if (!resp.ok) {
    let melding = `Serverfout (${resp.status}).`;
    try {
      const body = await resp.json();
      if (body?.fout) melding = body.fout;
    } catch {
      /* body was geen JSON — houd de standaardmelding */
    }
    throw maakFout(resp.status, melding);
  }

  return resp.json();
}

/**
 * Haal het EIGEN persoonlijke maandrooster op (identiteit uit het token via authHeaders).
 * @returns {Promise<object>} de server-JSON ({ rol, jaar, maand, medewerker, weken }).
 */
export async function haalMijnRooster(jaar, maand) {
  const qs = `jaar=${encodeURIComponent(jaar)}&maand=${encodeURIComponent(maand)}`;
  return haalJson(`/api/mijn-rooster?${qs}`, await authHeaders());
}

/**
 * Haal het volledige teammaandrooster op (alle collega's).
 * @returns {Promise<object>} de server-JSON ({ jaar, maand, weken:[{weeknummer,isoJaar,blok,dagen,conflicten}] }).
 */
export async function haalTeamRooster(jaar, maand) {
  const qs = `jaar=${encodeURIComponent(jaar)}&maand=${encodeURIComponent(maand)}`;
  return haalJson(`/api/rooster?${qs}`, await authHeaders());
}

/**
 * Haal het volledige data-object op (GET — tegenhanger van stuurData/PUT).
 * @returns {Promise<object>} de server-JSON: de tien gepersisteerde velden.
 */
// Revisie van de omgeving zoals de server hem het laatst meldde (bij een GET of als antwoord
// op een geslaagde PUT). Module-level en bewust NIET in de store: het is geen domeindata en
// hoort dus niet in de back-up, de partialize of localStorage. null = nooit een revisie
// gezien (server van vóór deze stap) → dan sturen we het veld niet mee en slaat de server
// zijn check over.
let laatsteRevisie = null;

export async function haalData() {
  const data = await haalJson('/api/data', await authHeaders());
  laatsteRevisie = Number.isFinite(data?.revisie) ? data.revisie : null;
  return data;
}

/**
 * Stuur het volledige data-object naar de server (PUT — vervangt de opslag).
 * @returns {Promise<object>} de server-JSON ({ ok: true }).
 */
export async function stuurData(data) {
  // Revisie meesturen zodat de server kan zien of we van de actuele stand uitgaan. Kennen we
  // er geen, dan laten we het veld weg — de server accepteert dat als overgangspad.
  const body = laatsteRevisie === null ? data : { ...data, revisie: laatsteRevisie };
  const resp = await fetch('/api/data', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  // 409 = een andere teamleider heeft intussen geschreven. Centraal melden (App toont de
  // overlay) i.p.v. de stille catch in serverSync: die zou dit wegslikken en de gebruiker met
  // een lokale stand laten doorwerken die nooit meer aankomt.
  if (resp.status === 409) {
    let info = {};
    try { info = await resp.json(); } catch { /* geen JSON-body — dan zonder details melden */ }
    dataConflict(info);
    throw maakFout(409, info.fout || 'Het rooster is inmiddels door iemand anders gewijzigd.');
  }
  if (!resp.ok) throw maakFout(resp.status, 'stuurData faalde');
  const uit = await resp.json();
  // De server hoogt de revisie op bij elke schrijfactie en geeft de nieuwe terug. Zonder dit
  // bij te werken zou onze volgende push een 409 tegen onze eigen schrijfactie opleveren.
  if (Number.isFinite(uit?.revisie)) laatsteRevisie = uit.revisie;
  return uit;
}

/**
 * Vraag de server wie de INGELOGDE gebruiker is: gekoppelde medewerker + rol + omgevingen.
 * Identiteit komt uit het token (authHeaders), niet meer uit een e-mailparameter.
 * @returns {Promise<object>} { gekoppeld:true, mwKey, naam, rollen, rol, omgevingen } of { gekoppeld:false }.
 */
export async function haalWieBenIk() {
  return haalJson('/api/wie-ben-ik', await authHeaders());
}

/**
 * Haal de dienstruil-verzoeken van de ingelogde gebruiker op (identiteit uit het token).
 * @returns {Promise<object>} { mijnVerzoeken, teBeoordelen, isTeamleider, mwKey }.
 */
export async function haalRuilverzoeken() {
  return haalJson('/api/ruilverzoeken', await authHeaders());
}

export async function haalMeldingen() {
  return haalJson('/api/meldingen', await authHeaders());
}

export async function markeerMeldingenGelezen() {
  return postJson('/api/meldingen/gelezen', {}, await authHeaders());
}

export async function verwijderMelding(id) {
  return postJson(`/api/meldingen/${id}/verwijder`, {}, await authHeaders());
}

export async function wisAlleMeldingen() {
  return postJson('/api/meldingen/wis-alles', {}, await authHeaders());
}

// ── centraal foutenlogboek ────────────────────────────────────────────────────
/**
 * Stuur een batch log-records naar de server. BEWUST een KALE fetch (niet via
 * haalJson/postJson): een mislukte log-POST mag NOOIT de centrale sessie-verlopen-
 * overlay triggeren — maakFout() vuurt die bij een 401 — en mag NOOIT een Error
 * gooien die ergens gelogd wordt, anders ontstaat de fout→log→POST→fout-lus.
 * @returns {Promise<boolean>} true bij een 2xx-antwoord, anders false. Gooit nooit.
 */
export async function stuurLogs(items) {
  try {
    const auth = await authHeaders();
    if (!auth.Authorization) return false; // geen token → niet versturen (server eist er één)
    const resp = await fetch('/api/logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...auth },
      body: JSON.stringify({ items }),
      keepalive: true,
    });
    return resp.ok;
  } catch {
    return false; // netwerk-/parse-fout → stil; logSync probeert het later opnieuw
  }
}

/** Haal ALLE server-logs op (alleen teamleider). Gooit een leesbare Error bij falen. */
export async function haalLogs() {
  return haalJson('/api/logs', await authHeaders());
}

/** Leeg het hele server-foutenlogboek (alleen teamleider). Gooit een Error bij falen. */
export async function wisLogsApi() {
  return postJson('/api/logs/wis-alles', {}, await authHeaders());
}

/**
 * Hoeveel logregels zijn er nieuw sinds IK het logboek opende (alleen teamleider). Bewust
 * licht: de server geeft alleen { aantal }, geen records — dit hangt aan de nav-badge en
 * draait dus bij elke navigatie.
 */
export async function haalNieuweLogs() {
  return haalJson('/api/logs/nieuw', await authHeaders());
}

/**
 * Stempel dat IK het logboek nu gezien heb (alleen teamleider); daarna is mijn teller 0.
 * Per persoon: het stempel van een andere teamleider blijft ongemoeid.
 */
export async function markeerLogsGezien() {
  return postJson('/api/logs/gezien', {}, await authHeaders());
}

/**
 * Naam van het browser-event dat LogViewer afgeeft zodra het gezien-stempel gezet is, zodat
 * de nav-badge meteen op nul kan. Woont hier omdat App.jsx én LogViewer.jsx allebei al uit
 * api.js importeren: een eigen module is ervoor overdreven, en de constante uit App.jsx
 * halen zou een import-lus geven (App → Beheer → LogViewer → App).
 */
export const LOGS_GEZIEN_EVENT = 'sass:logs-gezien';

// ── wijzigingslogboek (WIE veranderde WAT in het rooster) ─────────────────────
/**
 * Stuur een batch wijzigings-records naar de server. Anders dan stuurLogs is dit WEL een
 * gewone postJson: een mislukte melding is geen fout-over-een-fout, dus de normale
 * foutafhandeling mag hier gelden. De aanroeper (wijzigingSync) vangt alles stil af.
 * Het record bevat alleen rooster-gegevens; mwKey/naam/tijdstip stempelt de server.
 * @returns {Promise<object>} { ok: true, opgeslagen }.
 */
export async function stuurWijzigingen(items) {
  return postJson('/api/wijzigingen', { items }, await authHeaders());
}

/** Haal het hele wijzigingslogboek op (alleen teamleider). Gooit een leesbare Error bij falen. */
export async function haalWijzigingen() {
  return haalJson('/api/wijzigingen', await authHeaders());
}

/**
 * Naam van het browser-event dat wijzigingSync afgeeft zodra een batch met CEL-records
 * geslaagd is opgeslagen. serverSync luistert erop en haalt dan alleen `wijzigingen` opnieuw
 * op, zodat het handmatig-teken in de matrix verschijnt zonder F5.
 *
 * Zelfde constructie en dezelfde reden als LOGS_GEZIEN_EVENT hierboven: een browser-event is
 * hier het bestaande patroon, en het houdt wijzigingSync los van de store — dat bestand
 * importeert useRoosterStore bewust niet, en dat blijft zo.
 */
export const WIJZIGINGEN_OPGESLAGEN_EVENT = 'sass:wijzigingen-opgeslagen';

/**
 * Haal het wijzigingslogboek op ZONDER ooit te gooien en zonder de sessie-verlopen-overlay te
 * kunnen triggeren. BEWUST een kale fetch, precies zoals stuurLogs (r.165-187) en om dezelfde
 * reden: haalJson gaat via maakFout(), en die vuurt bij een 401 sessieVerlopen(). Deze call
 * draait automatisch na een bewerking, dus een verlopen token of een medewerker die hem
 * onbedoeld raakt (de route is teamleider-only, 403) mag NOOIT een blokkerende overlay
 * opleveren voor iets waar de gebruiker niet om gevraagd heeft.
 * @returns {Promise<Array|null>} de records, of null bij elke fout.
 */
export async function haalWijzigingenStil() {
  try {
    const auth = await authHeaders();
    if (!auth.Authorization) return null; // geen token → niet proberen
    const resp = await fetch('/api/wijzigingen', { headers: auth });
    if (!resp.ok) return null;            // 401/403/500 → stil, de stand blijft zoals hij was
    const body = await resp.json();
    return Array.isArray(body?.wijzigingen) ? body.wijzigingen : null;
  } catch {
    return null; // netwerk-/parse-fout → stil
  }
}

/** Leeg het hele wijzigingslogboek (alleen teamleider). Gooit een Error bij falen. */
export async function wisWijzigingenApi() {
  return postJson('/api/wijzigingen/wis-alles', {}, await authHeaders());
}

export async function verwijderRuilHistorieApi(id) {
  return postJson(`/api/ruilverzoeken/${id}/verwijder-historie`, {}, await authHeaders());
}

export async function wisRuilHistorieApi() {
  return postJson('/api/ruilverzoeken/wis-historie', {}, await authHeaders());
}

/**
 * Haal de komende gepubliceerde diensten van de ingelogde gebruiker op (identiteit uit token).
 * @returns {Promise<object>} { diensten: [...] } in de aangebodenDienst-vorm.
 */
export async function haalMijnDiensten() {
  return haalJson('/api/mijn-diensten', await authHeaders());
}

/**
 * Gedeelde POST + foutafhandeling (spiegelt haalJson, maar met methode POST + JSON-body).
 * Bij een serverfout een leesbare melding (uit body.fout) met de HTTP-status op err.status.
 */
async function postJson(pad, body, headers = {}) {
  let resp;
  try {
    resp = await fetch(pad, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('Kan de server niet bereiken. Draait de server (poort 3001)?');
  }

  if (!resp.ok) {
    let melding = `Serverfout (${resp.status}).`;
    let body = null;
    try {
      body = await resp.json();
      if (body?.fout) melding = body.fout;
    } catch {
      /* body was geen JSON — houd de standaardmelding */
    }
    const err = maakFout(resp.status, melding);
    // Volledige foutbody erbij: sommige endpoints geven naast `fout` ook velden waar de
    // aanroeper op moet kunnen handelen (bv. herhalenMogelijk bij /api/publiceer-melding).
    // Puur additief — bestaande aanroepers lezen alleen err.message en err.status.
    err.body = body;
    throw err;
  }

  return resp.json();
}

/**
 * Maak een ruilverzoek aan (medewerker-toegankelijk schrijf-endpoint). De server bepaalt de
 * indiener uit het token; payload = { aangebodenDienst, collegaKey, collegaNaam }.
 * @returns {Promise<object>} { ok: true, verzoek }.
 */
export async function maakRuilverzoekApi(payload) {
  return postJson('/api/ruilverzoeken', payload, await authHeaders());
}

/**
 * Trek je eigen ruilverzoek in (alleen de indiener; alleen zolang niet beoordeeld).
 * @returns {Promise<object>} { ok: true, verzoek }.
 */
export async function trekRuilverzoekInApi(id) {
  return postJson(`/api/ruilverzoeken/${id}/intrek`, {}, await authHeaders());
}

/**
 * Accepteer een ruilverzoek (alleen de gevraagde collega) met een gekozen tegendienst.
 * @returns {Promise<object>} { ok: true, verzoek }.
 */
export async function accepteerRuilverzoekApi(id, tegendienst) {
  return postJson(`/api/ruilverzoeken/${id}/accepteer`, { tegendienst }, await authHeaders());
}

/**
 * Weiger een ruilverzoek (alleen de gevraagde collega).
 * @returns {Promise<object>} { ok: true, verzoek }.
 */
export async function weigerRuilverzoekApi(id) {
  return postJson(`/api/ruilverzoeken/${id}/weiger`, {}, await authHeaders());
}

/**
 * Beoordeel een ruilverzoek (alleen de teamleider): akkoord=true keurt goed (de dienst-wissel
 * landt server-side in de momentopname), akkoord=false wijst af.
 * @returns {Promise<object>} { ok: true, verzoek }.
 */
export async function beoordeelRuilverzoekApi(id, akkoord) {
  return postJson(`/api/ruilverzoeken/${id}/beoordeel`, { akkoord }, await authHeaders());
}

/**
 * Stuur de "nieuw rooster staat klaar"-melding naar het team (alleen de teamleider).
 * De server weigert een tweede verzending voor dezelfde maand met een 409; die foutbody
 * ({ fout, herhalenMogelijk, eerder }) staat dan op err.body, zodat de aanroeper kan
 * bevestigen en met nogmaals=true kan herhalen.
 * @returns {Promise<object>} { ok: true, aantal, verstuurdOp }.
 */
export async function stuurPublicatieMelding(jaar, maand, nogmaals = false) {
  return postJson('/api/publiceer-melding', { jaar, maand, nogmaals }, await authHeaders());
}
